import { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Modal, Input, Select, Autocomplete } from '@/components/ui';
import { useCargoListFormOptions } from '@/hooks/useCargoListFormOptions';
import { CargoListService } from '@/services/cargoListService';
import { getWriteErrorMessage } from '@/utils/apiError';
import { SelectDefaultsSeeder } from './SelectDefaultsSeeder';
import { VesselTypeQuickSelect } from './VesselTypeQuickSelect';
import type { CargoVesselType } from '@/constants/cargoList.constants';
import type { CreateCargoFormValues, CreateCargoRequest } from '@/types/cargoList.types';

interface CreateCargoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  charterer: Yup.string().required('Charterer is required'),
  trade: Yup.string().required('Trade is required'),
  lcStart: Yup.date().required('Laycan start date is required'),
  lcEnd: Yup.date()
    .required('Laycan end date is required')
    .min(Yup.ref('lcStart'), 'End date must be after start date'),
  cargoSize: Yup.number()
    .typeError('Cargo size must be a number')
    .required('Cargo size is required')
    .min(40000, 'Cargo size must be at least 40,000 MT')
    .max(700000, 'Cargo size cannot exceed 700,000 MT'),
  status: Yup.number().required('Status is required'),
  certainty: Yup.number().required('Certainty is required'),
  privacyLevel: Yup.number().required('Privacy level is required'),
  laytime: Yup.number()
    .typeError('Laytime must be a number')
    .min(0, 'Laytime cannot be negative')
    .max(99999, 'Laytime cannot exceed 99999'),
  primaryComm: Yup.number()
    .typeError('Commission must be a number')
    .min(0, 'Commission cannot be negative')
    .max(9, 'Commission cannot exceed 9%'),
  addressComm: Yup.number()
    .typeError('Commission must be a number')
    .min(0, 'Commission cannot be negative')
    .max(9, 'Commission cannot exceed 9%'),
  primaryBroker: Yup.string().nullable(),
  rateAssessor: Yup.string().nullable(),
});

export function CreateCargoModal({ isOpen, onClose, onSuccess }: CreateCargoModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Not a form value - it is never sent to the API - so it is held here rather
  // than in Formik, and resets with the component when the modal closes.
  const [vesselType, setVesselType] = useState<CargoVesselType | null>(null);

  const {
    chartererOptions,
    brokerOptions,
    rateAssessorOptions,
    tradeOptions,
    privacyLevelOptions,
    fixtureStatusOptions,
    certaintyCategoryOptions,
    loading,
    error,
  } = useCargoListFormOptions();

  // Get default values from the first available options
  const getDefaultSelectValue = (options: Array<{ value: string | number; label: string }>) => {
    return options.length > 0 ? options[0].value : '';
  };

  const initialValues: CreateCargoFormValues = {
    charterer: '',
    trade: '',
    primaryBroker: '',
    rateAssessor: '',

    lcStart: '',
    lcEnd: '',

    status: getDefaultSelectValue(fixtureStatusOptions),
    certainty: getDefaultSelectValue(certaintyCategoryOptions),
    privacyLevel: getDefaultSelectValue(privacyLevelOptions),

    cargoSize: '',
    laytime: 24,
    primaryComm: 0,
    addressComm: 0,
  };

  const handleSubmit = async (
    values: CreateCargoFormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setSubmitError(null);

    try {
      // The Autocomplete sets the value to option.id on selection. Resolve by id
      // first, then fall back to name (which catches custom-typed values that
      // happen to match an existing option's name).
      const resolveId = (
        options: { id: number | string; name: string }[],
        value: unknown
      ): number | null => {
        const found = options.find((opt) => String(opt.id) === String(value) || opt.name === value);
        return found ? Number(found.id) : null;
      };

      const chartererId = resolveId(chartererOptions, values.charterer);
      const tradeId = resolveId(tradeOptions, values.trade);
      if (chartererId == null) {
        throw new Error('Charterer not found. Please pick one from the suggestions.');
      }
      if (tradeId == null) {
        throw new Error('Trade not found. Please pick one from the suggestions.');
      }

      const rateAssessorId = values.rateAssessor
        ? resolveId(rateAssessorOptions, values.rateAssessor)
        : null;
      if (values.rateAssessor && rateAssessorId == null) {
        throw new Error(
          'Rate assessor not found. Please pick one from the suggestions or clear the field.'
        );
      }
      const primaryBrokerId = values.primaryBroker
        ? resolveId(brokerOptions, values.primaryBroker)
        : null;
      if (values.primaryBroker && primaryBrokerId == null) {
        throw new Error(
          'Broker not found. Please pick one from the suggestions or clear the field.'
        );
      }

      const createRequest: CreateCargoRequest = {
        ReportedTime: new Date().toISOString(),
        TypeId: 1, // Default fixture type ID
        TradeId: tradeId,
        StatusId: Number(values.status),
        CertaintyId: Number(values.certainty),
        PrivacyLevelId: Number(values.privacyLevel),
        IsOnDailyReport: true,
        IsOnExternalDailyReport: false,
        ChartererId: chartererId,
        CargoSize: parseFloat(values.cargoSize),
        RateAssessorId: rateAssessorId,
        IsFixedDirect: false,
        BrokerDidNotShare: false,
        PrimaryBrokerId: primaryBrokerId,
        PrimaryBrokerCommission: Number(values.primaryComm) / 100,
        AddressCommission: Number(values.addressComm) / 100,
        LayCanStart: new Date(values.lcStart).toISOString(),
        LayCanEnd: new Date(values.lcEnd).toISOString(),
        Laytime: Number(values.laytime),
        Note: '',
      };

      await CargoListService.createCargoItem(createRequest);

      onSuccess?.();
      onClose();
    } catch (err) {
      setSubmitError(getWriteErrorMessage(err, 'Failed to create cargo'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Create New Cargo" size="xl">
      <div className="mx-auto max-w-4xl">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => {
            return (
              <Form className="space-y-6">
                {/* Fills the three selects once their options land. Replaces a
                    `key` on <Formik> that used to remount and wipe the form. */}
                <SelectDefaultsSeeder
                  status={fixtureStatusOptions[0]?.value}
                  certainty={certaintyCategoryOptions[0]?.value}
                  privacyLevel={privacyLevelOptions[0]?.value}
                />

                <VesselTypeQuickSelect value={vesselType} onSelect={setVesselType} />

                {submitError && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-600">{submitError}</p>
                  </div>
                )}

                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-600">Error loading form data: {error}</p>
                  </div>
                )}

                {/* Cargo Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Cargo Information
                  </h3>
                  <div className="space-y-4">
                    <Autocomplete
                      name="charterer"
                      label="Charterer"
                      placeholder="Search for charterer..."
                      options={chartererOptions}
                      loading={loading}
                    />

                    <Autocomplete
                      name="trade"
                      label="Trade"
                      placeholder="Search for trade..."
                      options={tradeOptions}
                      loading={loading}
                    />

                    <Input
                      name="cargoSize"
                      label="Cargo Size (MT)"
                      type="number"
                      min={40000}
                      max={700000}
                      placeholder="40,000 - 700,000"
                    />
                  </div>
                </div>

                {/* Laycan Schedule */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Laycan Schedule
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    <Input name="lcStart" label="Start" type="date" />
                    <Input name="lcEnd" label="End" type="date" />
                    <Input name="laytime" label="Laytime (Hrs)" type="number" placeholder="24" />
                  </div>
                </div>

                {/* Status & Classification */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Status & Classification
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    <Select name="status" label="Status" options={fixtureStatusOptions} />
                    <Select name="certainty" label="Certainty" options={certaintyCategoryOptions} />
                    <Select
                      name="privacyLevel"
                      label="Privacy Level"
                      options={privacyLevelOptions}
                    />
                  </div>
                </div>

                {/* Commercial Terms */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Commercial Terms
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input
                      name="primaryComm"
                      label="Primary Commission (%)"
                      type="number"
                      placeholder="0.00"
                      step="0.01"
                    />
                    <Input
                      name="addressComm"
                      label="Address Commission (%)"
                      type="number"
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Additional Information (Optional) */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Additional Information
                    <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                      (Optional)
                    </span>
                  </h3>
                  <div className="space-y-4">
                    <Autocomplete
                      name="primaryBroker"
                      label="Primary Broker"
                      placeholder="Search for broker"
                      options={brokerOptions}
                      loading={loading}
                    />

                    <Autocomplete
                      name="rateAssessor"
                      label="Rate Assessor"
                      placeholder="Search for assessor"
                      options={rateAssessorOptions}
                      loading={loading}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-4 border-t border-gray-200 pt-6 dark:border-gray-700">
                  <Button type="button" variant="outline" onClick={onClose} size="lg">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting} size="lg" className="min-w-36">
                    {isSubmitting ? 'Creating...' : 'Create Cargo'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
}
